import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import {
  clientKindOf,
  h5LayoutOf,
  isMobileUa,
  isPhone,
  parseViewOverride,
  type ViewOverride,
  type ViewportEnv,
} from '../ui/viewport.js';

const OVERRIDE_KEY = 'cg.h5';

function readEnv(): ViewportEnv {
  if (typeof window === 'undefined' || typeof document === 'undefined')
    return { width: 0, shortSide: 0, coarse: false, mobileUa: false };
  const sw = window.screen?.width ?? 0;
  const sh = window.screen?.height ?? 0;
  return {
    // clientWidth = 布局视口宽:页面内容再宽(1440 画板)它也不变;innerWidth 在安卓上会跟着缩放变
    width: document.documentElement.clientWidth || window.innerWidth || 0,
    shortSide: sw > 0 && sh > 0 ? Math.min(sw, sh) : 0,
    coarse: typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches,
    mobileUa: isMobileUa(navigator.userAgent ?? ''),
  };
}
function readOverride(): ViewOverride | null {
  try {
    const v = sessionStorage.getItem(OVERRIDE_KEY);
    return v === 'phone' || v === 'desktop' ? v : null;
  } catch {
    return null;
  }
}

/**
 * 视口 store(学生端 H5):`phone` = 这台设备是手机(判定在 ui/viewport.ts);
 * `h5` = 手机 且 当前路由有手机版式 —— 这时给 <html> 加 class「h5」,所有手机样式都写在 `.h5 …` 下面
 * (没这个 class 时一条都不生效,电脑端 1440 画板原样不动)。页面与组件要按端做差异时读 `h5`。
 * 路由每次切换由 router.afterEach 调 `onRoute` 告诉这里当前路由的 meta.h5。
 *
 * 响应式的只有「是不是手机」这一个布尔:窗口大小变了先重新量,结论没变就什么都不写 ——
 * 电脑上拖窗口大小不会触发任何页面重新计算(量到的宽高本身不做成响应式)。
 */
export const useViewport = defineStore('viewport', () => {
  const override = ref<ViewOverride | null>(readOverride());
  const phone = ref(isPhone(readEnv(), override.value));
  const routeH5 = ref<unknown>(undefined);

  const layout = computed(() => h5LayoutOf(routeH5.value, phone.value));
  /** 手机版式生效中 */
  const h5 = computed(() => layout.value === 'phone');
  /** 接口 / 埋点的端标识(x-client) */
  const client = computed(() => clientKindOf(phone.value));

  /** 重新量一遍;只有「是不是手机」的结论变了才写(转屏、分屏、?h5= 手动指定) */
  function measure() {
    const next = isPhone(readEnv(), override.value);
    if (next !== phone.value) phone.value = next;
  }
  function onRoute(metaH5: unknown) {
    if (metaH5 !== routeH5.value) routeH5.value = metaH5;
  }
  /** 地址里的 ?h5=…(调试 / 截图):只记在本标签页 */
  function applyQuery(v: unknown) {
    const next = parseViewOverride(v);
    if (next === undefined) return;
    const value = next === 'auto' ? null : next;
    if (value !== override.value) override.value = value;
    try {
      if (value) sessionStorage.setItem(OVERRIDE_KEY, value);
      else sessionStorage.removeItem(OVERRIDE_KEY);
    } catch {
      /* 隐私模式:只在本页内存里生效 */
    }
    measure();
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    if (typeof window.matchMedia === 'function')
      window.matchMedia('(pointer: coarse)').addEventListener?.('change', measure);
    watch(h5, (on) => document.documentElement.classList.toggle('h5', on), { immediate: true });
  }

  return { override, phone, layout, h5, client, measure, onRoute, applyQuery };
});
